import { useContext, useEffect, useMemo, useState, useCallback, createContext } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import type { Perimetre, Territoire, Commune } from "models";
import { communesService, territoiresService, perimetresService } from "services";
import { useSnackbarErrorHandler } from "utils/errorHandling";
import type { ErrorHandlingConfig } from "utils/errorHandling";
import ToastMessages from "constants/ToastMessages";
import { enqueueSnackbar } from "notistack";
import { competencesUtil } from "utils";
import { sortUtil } from "utils/sortUtil";

function getCommonErrorHandlerOptions(toastMessage: string): ErrorHandlingConfig {
  return {
    default: (_error) => {
      enqueueSnackbar({
        variant: "error",
        message: toastMessage,
      });
      console.error(_error);
    },
  };
}

export interface IMandatData {
  isLoading: boolean;
  communes: Array<Commune>;
  perimetres: Array<Perimetre>;
  territoires: Array<Territoire>;
}

const data: IMandatData = {
  isLoading: true,
  communes: [],
  perimetres: [],
  territoires: [],
};

const MandatDataContext = createContext<IMandatData>(data);

export function MandatDataProvider({ children }: Readonly<PropsWithChildren>): ReactNode {
  const [communes, setCommunes] = useState<Array<Commune>>([]);
  const [perimetres, setPerimetres] = useState<Array<Perimetre>>([]);
  const [territoires, setTerritoires] = useState<Array<Territoire>>([]);

  const { catchErrors: catchCommunesErrors, isLoading: isCommunesLoading } =
    useSnackbarErrorHandler(getCommonErrorHandlerOptions(ToastMessages.SEARCH_ERROR_COMMUNES));
  const { catchErrors: catchTerritoiresErrors, isLoading: isTerritoiresLoading } =
    useSnackbarErrorHandler(getCommonErrorHandlerOptions(ToastMessages.SEARCH_ERROR_TERRITOIRES));
  const { catchErrors: catchPerimetresErrors, isLoading: isPerimetresLoading } =
    useSnackbarErrorHandler(getCommonErrorHandlerOptions(ToastMessages.SEARCH_ERROR_PERIMETRES));

  const updateAll = useCallback((): void => {
    async function getCommunes(): Promise<void> {
      const _communes = await communesService.getAllInAlsaceMoselle();
      setCommunes(_communes);
    }

    async function getPerimetres(): Promise<void> {
      const _perimetres = await perimetresService.getAll();
      _perimetres.sort(
        sortUtil.combineMultipleSort(
          (a, b) => a.libelle.localeCompare(b.libelle),
          competencesUtil.sortPerimetresByCompetence
        )
      );
      setPerimetres(_perimetres);
    }

    async function getTerritoires(): Promise<void> {
      const _territoires = await territoiresService.getAll();
      setTerritoires(_territoires);
    }

    void Promise.all([
      catchCommunesErrors(getCommunes),
      catchTerritoiresErrors(getTerritoires),
      catchPerimetresErrors(getPerimetres),
    ]);
  }, [catchCommunesErrors, catchPerimetresErrors, catchTerritoiresErrors]);

  useEffect(() => {
    updateAll();
  }, [updateAll]);

  const data: IMandatData = useMemo(
    () => ({
      isLoading: isCommunesLoading || isTerritoiresLoading || isPerimetresLoading,
      communes,
      perimetres,
      territoires,
    }),
    [
      isCommunesLoading,
      isTerritoiresLoading,
      isPerimetresLoading,
      communes,
      perimetres,
      territoires,
    ]
  );

  return <MandatDataContext.Provider value={data}>{children}</MandatDataContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMandatData(): IMandatData {
  const context = useContext(MandatDataContext);

  if (context == null) {
    throw new Error("useMandatData must be used within a MandatDataProvider");
  }

  return context;
}
